import React, { useCallback, useEffect, useState } from 'react';
import { usePlanStore } from '../../store/planStore';
import { useUserPricingCurrency } from '../../hooks/useUserPricingCurrency';
import {
  DeactivatePlanModal,
  EditSubscriptionModal,
  SubscriptionPlanCard,
  SubscriptionToast,
} from './components';
import {
  buildUpdatePlanRequest,
  mapApiPlansToSubscription,
  mapPlanDetailToSubscriptionView,
} from './subscriptionData';
import { EditSubscriptionFormData, SubscriptionPlanView } from './types/subscription';
import { planService } from '../../services/planService';
import { useAuthStore } from '../../store/authStore';
import { canManagePlans } from '../../utils/rbac';
import './Plans.scss';

type ToastState = {
  title: string;
  message: string;
} | null;

const enrichPlansWithDetails = async (
  plans: SubscriptionPlanView[]
): Promise<SubscriptionPlanView[]> =>
  Promise.all(
    plans.map(async (plan) => {
      try {
        const detail = await planService.getPlanById(plan.id);
        return mapPlanDetailToSubscriptionView(detail, plan.subscribers);
      } catch {
        return plan;
      }
    })
  );

export const Plans: React.FC = () => {
  const role = useAuthStore((state) => state.role);
  const canManage = canManagePlans(role);
  const { plans: apiPlans, isLoading, error, fetchPlans } = usePlanStore();
  const { currency: displayCurrency } = useUserPricingCurrency();

  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlanView[]>([]);
  const [isEnriching, setIsEnriching] = useState(false);
  const [editPlanId, setEditPlanId] = useState<string | null>(null);
  const [deactivatePlan, setDeactivatePlan] = useState<SubscriptionPlanView | null>(null);
  const [toast, setToast] = useState<ToastState>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);

  useEffect(() => {
    fetchPlans().catch(() => undefined);
  }, [fetchPlans]);

  useEffect(() => {
    if (!apiPlans.length) {
      setSubscriptionPlans([]);
      return;
    }

    let cancelled = false;
    const basePlans = mapApiPlansToSubscription(apiPlans);

    const loadDetails = async () => {
      setIsEnriching(true);
      try {
        const enriched = await enrichPlansWithDetails(basePlans);
        if (!cancelled) {
          setSubscriptionPlans(enriched);
        }
      } finally {
        if (!cancelled) {
          setIsEnriching(false);
        }
      }
    };

    loadDetails();

    return () => {
      cancelled = true;
    };
  }, [apiPlans]);

  const handleEdit = useCallback((plan: SubscriptionPlanView) => {
    setEditPlanId(plan.id);
  }, []);

  const handleDeactivateClick = useCallback((plan: SubscriptionPlanView) => {
    if (plan.isDynamic) return;
    if (plan.isHidden) {
      setSubscriptionPlans((prev) =>
        prev.map((item) => (item.id === plan.id ? { ...item, isHidden: false } : item))
      );
      setToast({
        title: 'Plan Activated',
        message: `${plan.name} is now active and visible to users.`,
      });
      return;
    }
    setDeactivatePlan(plan);
  }, []);

  const handleSavePlan = useCallback(
    async (planId: string, data: EditSubscriptionFormData) => {
      setIsSaving(true);
      try {
        await planService.updatePlan(planId, buildUpdatePlanRequest(data));
        const refreshedPlan = await planService.getPlanById(planId);
        const updatedView = mapPlanDetailToSubscriptionView(
          refreshedPlan,
          subscriptionPlans.find((plan) => plan.id === planId)?.subscribers ?? 0
        );

        setSubscriptionPlans((prev) =>
          prev.map((plan) => (plan.id === planId ? updatedView : plan))
        );
        setEditPlanId(null);
        setToast({
          title: 'Plan Updated',
          message: `${refreshedPlan.name}'s details have been saved successfully.`,
        });
        await fetchPlans();
      } catch (err) {
        setToast({
          title: 'Update Failed',
          message: err instanceof Error ? err.message : 'Failed to save plan changes.',
        });
      } finally {
        setIsSaving(false);
      }
    },
    [fetchPlans, subscriptionPlans]
  );

  const handleDeactivate = useCallback(async () => {
    if (!deactivatePlan || deactivatePlan.isDynamic) return;

    setIsDeactivating(true);
    try {
      await new Promise((resolve) => window.setTimeout(resolve, 400));
      setSubscriptionPlans((prev) =>
        prev.map((plan) =>
          plan.id === deactivatePlan.id ? { ...plan, isHidden: true } : plan
        )
      );
      setDeactivatePlan(null);
      setToast({
        title: 'Plan Deactivated',
        message: `${deactivatePlan.name} has been deactivated and hidden from users.`,
      });
    } finally {
      setIsDeactivating(false);
    }
  }, [deactivatePlan]);

  const showLoading = isLoading || isEnriching;

  return (
    <div className="plans-page">

      <section className="plans-page__panel">
        <header className="plans-page__header">
          <h1>Subscription Management</h1>
          <p>Manage plans, pricing, and feature availability across your CRM ecosystem.</p>
        </header>

        <div className="plans-page__content">
          {showLoading ? (
            <div className="plans-grid plans-grid--loading" aria-label="Loading subscription plans">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="subscription-plan-card subscription-plan-card--skeleton" />
              ))}
            </div>
          ) : subscriptionPlans.length === 0 ? (
            <div className="plans-page__empty">No subscription plans found.</div>
          ) : (
            <div className="plans-grid">
              {subscriptionPlans.map((plan) => (
                <SubscriptionPlanCard
                  key={plan.id}
                  plan={plan}
                  displayCurrency={displayCurrency}
                  onEdit={handleEdit}
                  onDeactivate={handleDeactivateClick}
                  canManage={canManage}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {error && <div className="error-message">{error.message}</div>}

      <EditSubscriptionModal
        isOpen={Boolean(editPlanId)}
        planId={editPlanId}
        onClose={() => setEditPlanId(null)}
        onSave={handleSavePlan}
        isSaving={isSaving}
      />

      <DeactivatePlanModal
        isOpen={Boolean(deactivatePlan)}
        planName={deactivatePlan?.name ?? ''}
        onClose={() => setDeactivatePlan(null)}
        onConfirm={handleDeactivate}
        isLoading={isDeactivating}
      />

      {toast && (
        <SubscriptionToast
          title={toast.title}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};
